import { apiClient } from '~/shared/api/data-ingestion-api'
import type {
  CodeGroupFormValues,
  DeductionFormValues,
  PatientClaimFormValues,
  ServiceLineFormValues,
} from '../model'
import { isServiceLineEmpty } from './claim-validation'

/** Converts an API string currency value (in cents) to a number or null. */
export function parseCents(value: string | null | undefined): number | null {
  if (value === null || value === undefined) return null
  const parsed = Number(value)
  return Number.isNaN(parsed) ? null : parsed
}

export function findAmount(
  amounts: Array<apiClient.ServiceLineAmountInfoOutput> | undefined,
  type: string,
): apiClient.ServiceLineAmountInfoOutput | undefined {
  return amounts?.find((a) => a.type === type)
}

export function buildDeduction(
  a: apiClient.ServiceLineAmountInfoOutput | undefined,
): DeductionFormValues {
  const isPercentage = a?.amount_type === 'percentage'
  const raw = parseCents(a?.amount)
  return {
    amount: isPercentage && raw !== null ? Math.round(raw * 10000) / 100 : raw,
    amountType: isPercentage ? 'percentage' : 'cents',
    codeGroups: (a?.codes ?? []).map((c): CodeGroupFormValues => {
      const rawCodeAmount = parseCents(c.amount)
      return {
        key: c.key ?? null,
        carcs: c.carcs ?? [],
        rarcs: c.rarcs ?? [],
        payorSpecificCodes: c.payor_specific_codes ?? [],
        amount:
          isPercentage && rawCodeAmount !== null
            ? Math.round(rawCodeAmount * 10000) / 100
            : rawCodeAmount,
      }
    }),
  }
}

export function buildDefaultValues(claim: apiClient.PatientClaimResponse): PatientClaimFormValues {
  const serviceLines: ServiceLineFormValues[] = (claim.service_lines ?? []).map((sl) => {
    const amt = (type: string) => findAmount(sl.amounts, type)
    return {
      serviceLineId: sl.service_line_id ?? null,
      serviceDate: sl.service_date_from ?? '',
      cptCode: sl.cpt_code ?? '',
      billedAmount: parseCents(amt('billed_amount')?.amount),
      allowedAmount: parseCents(amt('allowed_amount')?.amount),
      discount: parseCents(amt('discount')?.amount),
      otherPlan: parseCents(amt('other_plan')?.amount),
      adjustments: buildDeduction(amt('adjustments')),
      deductible: buildDeduction(amt('deductible')),
      coInsurance: buildDeduction(amt('coinsurance')),
      copayment: buildDeduction(amt('copayment')),
      nonCovered: buildDeduction(amt('non_covered')),
    }
  })

  return {
    patientAccountNumber: claim.patient_account_number ?? '',
    totalPaid: claim.total_paid ? Number(claim.total_paid) : null,
    patientFirstName: claim.patient_first_name ?? '',
    patientLastName: claim.patient_last_name ?? '',
    patientDob: claim.patient_dob ?? '',
    claimNumber: claim.claim_number ?? '',
    memberId: claim.member_id ?? '',
    pageOfDoc: claim.page_of_doc != null ? String(claim.page_of_doc) : '',
    localFlag: null,
    serviceLines:
      serviceLines.length > 0
        ? serviceLines
        : [
            {
              serviceLineId: null,
              serviceDate: '',
              cptCode: '',
              billedAmount: null,
              allowedAmount: null,
              discount: null,
              otherPlan: null,
              adjustments: { amount: null, amountType: 'cents', codeGroups: [] },
              deductible: { amount: null, amountType: 'cents', codeGroups: [] },
              coInsurance: { amount: null, amountType: 'cents', codeGroups: [] },
              copayment: { amount: null, amountType: 'cents', codeGroups: [] },
              nonCovered: { amount: null, amountType: 'cents', codeGroups: [] },
            },
          ],
  }
}

type AmountType = apiClient.ServiceLineAmountInfoInput['type']

function pushAmount(
  amounts: apiClient.ServiceLineAmountInfoInput[],
  type: AmountType,
  value: number | null,
) {
  if (value !== null) amounts.push({ type, amount: value })
}

function pushDeduction(
  amounts: apiClient.ServiceLineAmountInfoInput[],
  type: AmountType,
  d: DeductionFormValues,
) {
  if (d.amount === null) return
  amounts.push({
    type,
    amount: d.amountType === 'percentage' ? Math.round(d.amount * 100) / 10000 : d.amount,
    amount_type: d.amountType,
    codes: d.codeGroups.map((g) => ({
      key: g.key,
      carcs: g.carcs,
      rarcs: g.rarcs,
      payor_specific_codes: g.payorSpecificCodes,
      amount:
        g.amount === null
          ? null
          : d.amountType === 'percentage'
            ? Math.round(g.amount * 100) / 10000
            : g.amount,
    })),
  })
}

function buildServiceLineRequest(sl: ServiceLineFormValues): apiClient.ServiceLineRequest {
  const amounts: apiClient.ServiceLineAmountInfoInput[] = []
  pushAmount(amounts, 'billed_amount', sl.billedAmount)
  pushAmount(amounts, 'allowed_amount', sl.allowedAmount)
  pushAmount(amounts, 'discount', sl.discount)
  pushAmount(amounts, 'other_plan', sl.otherPlan)
  pushDeduction(amounts, 'adjustments', sl.adjustments)
  pushDeduction(amounts, 'deductible', sl.deductible)
  pushDeduction(amounts, 'coinsurance', sl.coInsurance)
  pushDeduction(amounts, 'copayment', sl.copayment)
  pushDeduction(amounts, 'non_covered', sl.nonCovered)
  return {
    service_line_id: sl.serviceLineId,
    cpt_code: sl.cptCode,
    service_date_from: sl.serviceDate || null,
    amounts,
  }
}

/** Maps form values to the API request body shape used by both create and update endpoints. */
export function buildRequestBody(
  values: PatientClaimFormValues,
): apiClient.PatientClaimCreateRequest {
  const pageOfDoc = values.pageOfDoc ? Number(values.pageOfDoc) : null
  return {
    patient_account_number: values.patientAccountNumber,
    total_paid: values.totalPaid,
    patient_first_name: values.patientFirstName || null,
    patient_last_name: values.patientLastName || null,
    patient_dob: values.patientDob || null,
    claim_number: values.claimNumber || null,
    member_id: values.memberId || null,
    service_provider_name: null,
    page_of_doc: Number.isNaN(pageOfDoc) ? null : pageOfDoc,
    flagged: false,
    adjustments: [],
    service_lines: values.serviceLines
      .filter((sl) => !isServiceLineEmpty(sl))
      .map(buildServiceLineRequest),
  }
}
